import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { PrivateShowRequestSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

export function PrivateShowRequestsCard(): ReactElement {
  const { api } = useAuth();
  const [requests, setRequests] = useState<PrivateShowRequestSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActing, setIsActing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listPrivateShowRequests();
    setRequests(result.requests);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.privateRequests.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAccept(requestId: string): Promise<void> {
    setIsActing(requestId);
    setError(null);
    try {
      await api.acceptPrivateShowRequest(requestId);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t('common.studio.privateRequests.actionError'),
      );
    } finally {
      setIsActing(null);
    }
  }

  async function handleDecline(requestId: string): Promise<void> {
    setIsActing(requestId);
    setError(null);
    try {
      await api.declinePrivateShowRequest(requestId);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t('common.studio.privateRequests.actionError'),
      );
    } finally {
      setIsActing(null);
    }
  }

  if (isLoading) {
    return (
      <p style={{ color: 'var(--fm-color-text-muted)' }}>
        {t('common.studio.privateRequests.loading')}
      </p>
    );
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.privateRequests.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.privateRequests.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      {requests.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.privateRequests.empty')}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {requests.map((request) => (
            <li
              key={request.id}
              style={{
                padding: '0.75rem 0',
                borderBottom: '1px solid var(--fm-color-border)',
              }}
            >
              <div style={{ fontWeight: 650, marginBottom: '0.25rem' }}>
                @{request.fromUsername} · {request.type} · {request.durationMinutes}m ·{' '}
                {request.offeredCoins} coins
              </div>
              {request.scheduledAt ? (
                <p style={{ margin: '0 0 0.35rem', fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }}>
                  {t('common.studio.privateRequests.scheduled').replace(
                    '{{datetime}}',
                    new Date(request.scheduledAt).toLocaleString(),
                  )}
                </p>
              ) : null}
              <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem' }}>{request.description}</p>
              {request.hardNos ? (
                <p style={{ margin: '0 0 0.35rem', fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }}>
                  {t('common.studio.privateRequests.hardNos').replace('{{text}}', request.hardNos)}
                </p>
              ) : null}
              {request.closetItemKeys.length > 0 ? (
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }}>
                  {t('common.studio.privateRequests.closet').replace(
                    '{{items}}',
                    request.closetItemKeys.join(', '),
                  )}
                </p>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <Button
                  label={t('common.studio.privateRequests.accept')}
                  disabled={isActing === request.id}
                  onClick={() => {
                    void handleAccept(request.id);
                  }}
                />
                <Button
                  label={t('common.studio.privateRequests.decline')}
                  variant="ghost"
                  disabled={isActing === request.id}
                  onClick={() => {
                    void handleDecline(request.id);
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
